import { useState, useEffect } from 'react';
import CreateFormTopBar from '../../../../../Components/TopBar/CreateFormTopBar';
import { TOPBAR_CONFIGS } from '../../../../../Components/TopBar/topBarConfigs';
import Select from 'react-select';
import TaskService from '../../../../../AppSettings/services/TaskService';
import MatterService from '../../../../../AppSettings/services/MatterService';
import EmployeeService from '../../../../../AppSettings/services/EmployeeService';
import './MatterCreateTasksPage.css';
import FormEntryMultilineTextbox from '@frontend/Components/Forms/Entry/MultilineTextbox/FormEntryMultilineTextbox';
import { useParams } from 'react-router-dom';
import { isLegalEnvironment } from '../../../../../AppSettings/environmentConfig';
import useActiveEmployeeId from '@frontend/AppSettings/hooks/useActiveEmployeeId';

const MatterCreateTasksPage = () => {
  const employeeId = useActiveEmployeeId();
  const { matterId } = useParams();
  const isLegalEnv = isLegalEnvironment;

  const taskTypes = [
    { value: 'Internal', label: 'Internal' },
    { value: 'External', label: 'External' },
  ];

  const [formData, setFormData] = useState({
    matterid: matterId,
    task_type: '',
    assignedto: '',
    duedate: '',
    description: '',
    task_title: '',
    status: 'Pending', // Default status
    createdby: employeeId ?? '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [matters, setMatters] = useState([]);
  const [employees, setEmployees] = useState([]); // Store fetched employees
  const [loadingMatters, setLoadingMatters] = useState(true);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [currentEmployee, setCurrentEmployee] = useState(null);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      matterid: matterId,
      createdby: employeeId ?? '',
    }));
  }, [employeeId, matterId]);

  // Separate useEffect for fetching matters
  useEffect(() => {
    const fetchMatters = async () => {
      if (!currentEmployee || !employeeId) return; // Don't fetch if we don't have employee data yet

      try {
        setLoadingMatters(true);
        let data;

        // Check permission level
        const permissionId = parseInt(currentEmployee.permish_id);
        console.log('Fetching matters with permission level:', permissionId);

        if (permissionId === 1) {
          console.log('Fetching all matters for admin');
          data = await MatterService.getMatters();
        } else if (permissionId === 2 || permissionId === 3) {
          // Regular users can only see their matters
          console.log('Fetching user-specific matters');
          data = await MatterService.getProjectMattersByEmployee(employeeId);
        }

        if (data) {
          const options = data.map((matter) => ({
            value: matter.matterid,
            label: `${matter.matterid}: ${matter.mattername}`,
          }));
          setMatters(options);
        }
      } catch (error) {
        console.error('Error fetching matters:', error);
        setError('Failed to load matters.');
      } finally {
        setLoadingMatters(false);
      }
    };

    fetchMatters();
  }, [currentEmployee, employeeId]);

  useEffect(() => {
    const fetchEmployees = async () => {
      if (!employeeId) {
        return;
      }
      try {
        const data = await EmployeeService.getEmployees(); // Fetch employees
        const options = data.map((employee) => ({
          value: employee.EmployeeID,
          label: `${employee.FirstName} ${employee.LastName}`,
        }));
        console.log(options);
        setEmployees(options);

        // Set current employee based on oid
        const currentEmp = data.find((emp) => emp.EmployeeID === employeeId);
        if (currentEmp) {
          setCurrentEmployee(currentEmp);
        }
      } catch (error) {
        console.error('Error fetching employees:', error);
        setError('Failed to load employees.');
      } finally {
        setLoadingEmployees(false);
      }
    };

    fetchEmployees();
  }, [employeeId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleMatterChange = (selectedOption) => {
    setFormData({
      ...formData,
      matterid: selectedOption ? selectedOption.value : '',
    });
  };

  const handleTaskTypeChange = (selectedOption) => {
    setFormData({
      ...formData,
      task_type: selectedOption ? selectedOption.value : '',
    });
  };

  const handleAssignedToChange = (selectedOption) => {
    setFormData({
      ...formData,
      assignedto: selectedOption ? selectedOption.value : '',
    });
  };

  const handleTaskCreation = async () => {
    setLoading(true);
    setError('');

    try {
      if (!employeeId) {
        throw new Error('Cannot create a task without an employee id.');
      }
      // Simulate task creation (you can call the actual TaskService.addTask here)
      console.log(formData);
      const response = await TaskService.addTask(formData);
      if (response.error) throw new Error(response.error);

      alert('Task added successfully!');

      // Clear all Select components by resetting the formData state
      setFormData({
        matterid: matterId,
        task_type: '',
        assignedto: '',
        duedate: '',
        description: '',
        task_title: '',
        status: 'Pending',
        createdby: employeeId ?? '',
      });
    } catch (error) {
      console.error('Error adding task:', error);
      setError('Failed to add the task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="Tasks-Page">
      <CreateFormTopBar
        config={isLegalEnv ? TOPBAR_CONFIGS.MATTER_TASKS : TOPBAR_CONFIGS.PROJECT_TASKS}
        onSave={handleTaskCreation}
        formData={formData}
        loading={loading}
        pageContext={{ matterId }}
      />
      <div
        className="Tasks-entire-form-container"
        style={{ justifyContent: 'center' }}
      >
        <div className="Tasks-form-container">
          <div className="Tasks-form-group">
            <label className="Tasks-form-group-label">Task Title</label>
            <div className="Tasks-input-icon">
              <input
                type="text"
                name="task_title"
                placeholder="Enter task title"
                value={formData.task_title}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="Tasks-form-group">
            <label className="Tasks-form-group-label">
              {isLegalEnv ? 'Matter' : 'Project'}
            </label>
            <Select
              options={matters}
              value={
                matters.find((matter) => matter.value === formData.matterid) ||
                null
              }
              onChange={handleMatterChange}
              isLoading={loadingMatters}
              placeholder={
                isLegalEnv
                  ? 'Select or search a matter...'
                  : 'Select or search a project...'
              }
              isClearable
            />
          </div>

          <div className="Tasks-form-group">
            <label className="Tasks-form-group-label">Assign Leader</label>
            <Select
              options={employees}
              isLoading={loadingEmployees}
              value={
                employees.find((emp) => emp.value === formData.assignedto) ||
                null
              }
              onChange={handleAssignedToChange}
              placeholder="Select or search an employee..."
              isClearable
            />
          </div>

          <div style={{ paddingBottom: '1em' }}>
            <div className="Tasks-item2">
              <label className="Tasks-form-group-label">Due Date</label>
              <div className="Tasks-input-icon">
                <input
                  type="date"
                  name="duedate"
                  value={formData.duedate}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <div className="Tasks-form-group">
            <label className="Tasks-form-group-label">Description</label>
            <FormEntryMultilineTextbox
              name="description"
              placeholder="Type the task description"
              value={formData.description}
              onChange={handleInputChange}
            />
          </div>

          <div className="Tasks-form-group">
            <label className="Tasks-form-group-label">Task Type</label>
            <Select
              options={taskTypes}
              value={
                taskTypes.find((type) => type.value === formData.task_type) ||
                null
              }
              onChange={handleTaskTypeChange}
              placeholder="Select or search a task type..."
              isClearable
            />
          </div>

          {error && <div className="error-message">{error}</div>}
          {loading && <div className="loading-message">Adding task...</div>}
        </div>
      </div>
    </div>
  );
};

export default MatterCreateTasksPage;
